import React, { useEffect, useRef, useState } from 'react'
import { useParams } from "react-router-dom";
import { FormeoRenderer } from 'formeo';
import intakeFormService from '@/services/intake-form/intakeFormService';
import { Skeleton } from '@/components/ui/skeleton';
import { injectCheckboxOtherOptions, applyCheckboxRequiredAttributes } from './checkboxOther';
import Select from 'react-select';
import { createRoot } from 'react-dom/client';
import { getGlobalSelectClass } from '@/utils';
import { toast } from 'sonner';
import { buildIntakeSubmission, getRequiredFieldsFromSchema } from './intakeFormSubmit';
import { showFieldProblems } from './fieldProblems';
import { applyInputKinds, enhanceSelectPlaceholders, getInputKinds } from './fieldInputs';

const buildSelectOptionsSignature = (options) =>
    options.map((o) => `${o.value}::${o.label}`).join("|");

const decodeHtml = (html) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
};

const parseFormFields = (rawFields) => {
    if (!rawFields) return rawFields;
    if (typeof rawFields !== "string") return rawFields;

    try {
        return JSON.parse(rawFields);
    } catch (rawParseError) {
        const decoded = decodeHtml(rawFields);
        if (decoded === rawFields) throw rawParseError;
        return JSON.parse(decoded);
    }
};

const enhanceMultiSelects = (container) => {
    if (!container) return;

    const selects = container.querySelectorAll('select[multiple]');

    selects.forEach((select) => {
        const options = Array.from(select.options).map((opt) => ({
            label: opt.text,
            value: opt.value,
        }));
        const optionsSig = buildSelectOptionsSignature(options);

        const prevSig = select.dataset.optionsSignature;
        const isEnhanced = select.dataset.enhanced === 'true';

        if (isEnhanced && prevSig !== optionsSig) {
            const reactSelectId = select.dataset.reactSelectId;
            if (reactSelectId) {
                const mount = select.parentNode?.querySelector(
                    `[data-react-select-for="${reactSelectId}"]`
                );
                if (mount) mount.remove();
            }
            delete select.dataset.enhanced;
        }

        if (select.dataset.enhanced === 'true') return;

        select.dataset.enhanced = 'true';
        select.dataset.optionsSignature = optionsSig;

        const reactSelectId =
            select.dataset.reactSelectId ||
            `rs_${Math.random().toString(36).slice(2, 9)}`;
        select.dataset.reactSelectId = reactSelectId;

        const mount = document.createElement('div');
        mount.className = 'react-select-wrapper';
        mount.dataset.reactSelectFor = reactSelectId;

        select.classList.add('hidden-native-select');
        select.tabIndex = -1;
        select.setAttribute('aria-hidden', 'true');

        select.parentNode.insertBefore(mount, select.nextSibling);

        // Ensure preview doesn't show any preselected options
        Array.from(select.options).forEach((opt) => {
            opt.selected = false;
        });
        select.selectedIndex = -1;

        const root = createRoot(mount);

        root.render(
            <Select
                classNames={getGlobalSelectClass()}
                isMulti
                options={options}
                className='input'
                classNamePrefix="react-select"
                placeholder={select.getAttribute('placeholder') || 'Select options...'}
                onChange={(selected) => {
                    const values = (selected || []).map((s) => s.value);

                    Array.from(select.options).forEach((opt) => {
                        opt.selected = values.includes(opt.value);
                    });

                    select.dispatchEvent(new Event('change', { bubbles: true }));
                }}
            />
        );
    });
};

const enhanceRenderedSingleCheckboxPreview = (container) => {
    if (!container) return;

    const otherRows = container.querySelectorAll(".formeo-other-option[data-other-for]");

    otherRows.forEach((otherRow) => {
        const parent = otherRow.parentElement;
        if (!parent) return;

        const directCheckboxes = Array.from(
            parent.querySelectorAll(':scope > input[type="checkbox"]')
        );

        if (directCheckboxes.length !== 1) return;

        const checkbox = directCheckboxes[0];
        const label =
            parent.querySelector(`:scope > label[for="${CSS.escape(checkbox.id)}"]`) ||
            checkbox.nextElementSibling;

        if (!(label instanceof HTMLLabelElement)) return;

        let pairWrap = parent.querySelector('[data-single-checkbox-pair="true"]');
        if (!pairWrap) {
            pairWrap = document.createElement("div");
            pairWrap.dataset.singleCheckboxPair = "true";
            pairWrap.style.setProperty("display", "inline-flex");
            pairWrap.style.setProperty("align-items", "center");
            pairWrap.style.setProperty("gap", "0.5rem");
            pairWrap.style.setProperty("flex-wrap", "wrap");
            parent.insertBefore(pairWrap, checkbox);
        }

        if (checkbox.parentElement !== pairWrap) {
            pairWrap.appendChild(checkbox);
        }

        if (label.parentElement !== pairWrap) {
            pairWrap.appendChild(label);
        }

        parent.style.setProperty("display", "flex");
        parent.style.setProperty("flex-direction", "column");
        parent.style.setProperty("align-items", "flex-start");
        parent.style.setProperty("gap", "0.5rem");

        otherRow.style.setProperty("margin-top", "0");
    });
};

const IntakeFormPreview = () => {
    const [fetchedForm, setFetchedForm] = useState(null)
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)

    const formContainerRef = useRef(null)
    const parsedFieldsRef = useRef(null)
    const requiredFieldsRef = useRef([])
    const { id } = useParams();

    const fetchIntakeForm = async () => {
        try {
            const { data, status } = await intakeFormService.getSingleForm(id);
            if (status) {
                setFetchedForm(data);
            }
        } catch (error) {
            console.error('Error Fetching Intake Form', error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchIntakeForm()
    }, [id])


    useEffect(() => {
        if (!fetchedForm || !formContainerRef.current) return;

        try {
            const parsedFields = parseFormFields(fetchedForm.fields);

            console.log("Parsed fields:", parsedFields);

            parsedFieldsRef.current = parsedFields;
            requiredFieldsRef.current = getRequiredFieldsFromSchema(parsedFields);

            const renderer = new FormeoRenderer({
                renderContainer: formContainerRef.current,
            });

            renderer.render(parsedFields);
            requestAnimationFrame(() => {
                injectCheckboxOtherOptions(formContainerRef.current, parsedFields);
                enhanceRenderedSingleCheckboxPreview(formContainerRef.current);
                applyCheckboxRequiredAttributes(formContainerRef.current, parsedFields);
                applyInputKinds(formContainerRef.current, getInputKinds(parsedFields));
                enhanceSelectPlaceholders(formContainerRef.current, { selector: 'select:not([multiple])' });
                enhanceMultiSelects(formContainerRef.current);
            });
        } catch (err) {
            console.error("Failed to parse form fields:", err);
        }
    }, [fetchedForm]);

    const handleSubmit = async () => {
        if (submitting || !formContainerRef.current) return;

        const { missing, errors, problems, fd } = buildIntakeSubmission({
            container: formContainerRef.current,
            schema: parsedFieldsRef.current,
            intakeFormId: fetchedForm?.id,
            requiredFields: requiredFieldsRef.current,
        });

        showFieldProblems(formContainerRef.current, problems);

        if (missing.length) {
            toast.warning('Please fill all required fields.', {
                description: missing.join(', '),
            });
            return;
        }

        if (errors.length) {
            errors.forEach((message) => toast.error(message));
            return;
        }

        try {
            setSubmitting(true);

            const tokenRes = await intakeFormService.generateTokenForIframe(fetchedForm?.id);
            const token = tokenRes?.data;
            if (!token) throw new Error('Could not generate submission token');

            const { status } = await intakeFormService.postIntakeForm(fd, token);
            if (!status) throw new Error();

            toast.success('Form submitted successfully.');
        } catch (err) {
            console.error(err);
            toast.error('Submission failed', {
                description: 'Please try again later.',
            });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="w-full h-full">
                <div className="card container-fixed mx-auto mt-6 !p-0">
                    <div className="card-header">
                        <h3 className="card-title">
                            <Skeleton className="h-5 w-52 dark:bg-gray-200 bg-gray-300 rounded-lg" />
                        </h3>
                    </div>
                    <div className="card-body space-y-4">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((section) => (
                            <div key={section}>
                                <Skeleton className="h-3.5 w-32 dark:bg-gray-200 bg-gray-300 rounded-lg" />
                                <Skeleton className="h-10 w-full dark:bg-gray-200 bg-gray-300 rounded-lg mt-2" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="card">
            {/* <div className="card-header">
                <h3 className="card-title">{fetchedForm?.name}</h3>
            </div> */}
            <div className="card-body">
                <div ref={formContainerRef} className="formeo-renderer-container" />
                <div className="flex justify-end mt-4">
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSubmit}
                        disabled={submitting}
                    >
                        {submitting ? 'Submitting...' : (fetchedForm?.submit_button_label || 'Submit Form')}
                    </button>
                </div>
            </div>
        </div>
    )
}

export { IntakeFormPreview }
